import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import { IPrintEvalData } from './JudgePortalTypes';
import './JudgePortal.css';

const optionLabel = (labels: Record<string, string>, i: number) => {
  if (i === 0) return labels.exemplary || 'Exemplary';
  if (i === 1) return labels.proficient || 'Proficient';
  if (i === 2) return labels.developing || 'Developing';
  if (i === 3) return labels.novice || 'Novice';
  return labels.notEvident || 'Not Evident';
};

const EvalPrinterPage = () => {
  const [data, setData] = useState<IPrintEvalData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const roundId = Number(StoreX.GetQueryStringValue('roundId') || 0);
    const entryId = Number(StoreX.GetQueryStringValue('entryId') || 0);
    const roomId = Number(StoreX.GetQueryStringValue('roomId') || 0);
    store.server
      .postApi<ServerResponse<IPrintEvalData>>('../JudgePortal/PrintEvalPageData', {
        roundId,
        entryId,
        roomId,
      })
      .then((x) => {
        if (x.Success && x.Value) {
          setData(x.Value);
        } else {
          toast.error(x.Message || 'Unable to load eval sheets.', { autoClose: false });
          setData(null);
        }
      })
      .catch(() => {
        toast.error('Unable to load eval sheets.', { autoClose: false });
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="eval-printer-page" data-testid="eval-printer-page">
        <p className="no-print">Loading eval sheets…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="eval-printer-page" data-testid="eval-printer-page">
        <div className="alert alert-warning no-print">Eval sheets are not available.</div>
      </div>
    );
  }

  const labels = data.labels || {};

  return (
    <div className="eval-printer-page" data-testid="eval-printer-page">
      <div className="no-print" style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>
      {(data.sheets || []).length === 0 && (
        <div className="alert alert-info no-print">No evaluations found for this selection.</div>
      )}
      {(data.sheets || []).map((sheet, idx) => (
        <div className="eval" key={idx}>
          <div className="heading">
            <div className="flex">
              <img
                src="/api/fileapi/filefetcherclient/e99bb006-500d-40fb-82df-6946fe3e383a"
                alt="logo"
                className="logo"
              />
              <div className="eval-name">{sheet.type}</div>
            </div>
            <div>
              {sheet.roomBuilding} {sheet.roomName}
              {data.showSchoolCode && sheet.schoolCode && <div>{sheet.schoolCode}</div>}
            </div>
            <div className="text-right">
              {sheet.entryNumber}
              <br />
              {sheet.judge}
            </div>
          </div>
          <div className="title">{sheet.title}</div>
          <div>
            {!sheet.finals &&
              (sheet.questionGroups || []).map((group, gi) => (
                <div className="eval-group" key={gi}>
                  <div className="title">{group.text}</div>
                  {(group.questions || []).map((question, qi) => (
                    <div className="eval-question" key={qi}>
                      <div className="question">{question.text}</div>
                      <ul>
                        {(question.options || []).map((option, i) => (
                          <li key={i} className={question.selected === i ? 'selected' : ''}>
                            {optionLabel(labels, i)} - {option}
                          </li>
                        ))}
                        <li className={question.selected === 4 ? 'selected' : ''}>
                          {labels.notEvident || 'Not Evident'}
                        </li>
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            {!sheet.finals && (
              <div className="eval-group">
                <div className="title">{labels.rulesHeading || 'Rules'}</div>
                {(sheet.rules || []).map((rule, ri) => (
                  <div className="eval-rule" key={ri}>
                    <div className="rule">{rule.text}</div>
                    <div className="rule-response">
                      <div>
                        <span className={rule.selected === true ? 'selected' : ''}>
                          {labels.ruleYes || 'Yes'}
                        </span>
                        <span className={rule.selected === false ? 'selected' : ''}>
                          {labels.ruleNo || 'No'}
                        </span>
                        {rule.ruleType === 'TrueFalseNA' && (
                          <span className={rule.selected == null ? 'selected' : ''}>
                            {labels.ruleNa || 'N/A'}
                          </span>
                        )}
                      </div>
                      {rule.selected === false && rule.reason && (
                        <div className="rule-reason">{rule.reason}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="eval-group">
              <div className="title">Comments</div>
              <div className="comments">{sheet.commentsGood}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default EvalPrinterPage;
